import React, { useEffect, useRef, useState } from 'react';
import { fetchCaptchaConfig, type CaptchaConfig } from '../api/authApi';

/**
 * Bot challenge for the public forms (signup, password reset).
 *
 * Renders nothing when the server reports no provider, so local development
 * and tests are unaffected. With Turnstile configured it loads Cloudflare's
 * script once, renders the widget, and hands the one-time token up through
 * `onToken`. The token is cleared when the widget expires, so a stale token
 * is never submitted.
 */

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

let configPromise: Promise<CaptchaConfig> | null = null;
export function loadConfig(): Promise<CaptchaConfig> {
  if (!configPromise) {
    configPromise = fetchCaptchaConfig().catch(() => ({ provider: 'none', site_key: null }));
  }
  return configPromise;
}

let scriptPromise: Promise<void> | null = null;
function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = TURNSTILE_SCRIPT;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Could not load the verification widget'));
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

interface Props {
  onToken: (token: string | null) => void;
  /** Bump to force a fresh challenge (e.g. after a failed submit). */
  resetKey?: number;
}

export function Captcha({ onToken, resetKey = 0 }: Props) {
  const [config, setConfig] = useState<CaptchaConfig | null>(null);
  const [failed, setFailed] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadConfig().then((c) => { if (!cancelled) setConfig(c); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!config || config.provider !== 'turnstile' || !config.site_key || !container.current) return;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !container.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(container.current, {
          sitekey: config.site_key,
          theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',   // follow the app theme (rendered light on the dark sign-up form before)
          callback: (token: string) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
    // onToken is intentionally not a dependency: re-rendering the widget on
    // every parent render would discard the user's completed challenge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  useEffect(() => {
    if (resetKey && widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
      onToken(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!config || config.provider === 'none') return null;
  if (failed) {
    return <div role="alert" style={{ fontSize: '0.85rem', color: 'var(--color-danger, #b00020)' }}>
      The verification widget could not be loaded. Please disable content blockers and reload.
    </div>;
  }
  return <div ref={container} style={{ margin: '0.75rem 0' }} />;
}

export default Captcha;
